import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { downloadCSV, Title, useNotify } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { fmtDate } from '../../shared/Panel';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { menuLabel } from '../menuConfig';
import { FeatureGate, useFeatureFlags } from './FeatureGate';
import { GuideFlagButton } from '../../guide/GuideFlagButton';
import { FeatureBundlesPanel } from './FeatureBundlesPanel';
import { useNarrowLayout } from '../useNarrowLayout';
import {
  fetchDecisionHistory,
  fetchFlagAccess,
  fetchFlagHistory,
  fetchFlags,
  flagStateLabel,
  probeTrialRoute,
  reloadEffectiveFlags,
  saveDecision,
  saveFlag,
  VERSION_LABELS,
  type DecisionChange,
  type DecisionStatus,
  type FeatureFlag,
  type FlagChange,
} from './featureFlagsApi';
import {
  DECISION_COLORS,
  DECISION_LABELS,
  DECISION_STATUSES,
  decisionCounts,
  decisionCountsText,
  decisionError,
  decisionHint,
  decisionLabel,
  decisionOf,
  decisionsCsv,
  MAX_DECISION_COMMENT_LENGTH,
} from './featureFlagDecisions';

/**
 * „Przełączniki funkcji” (decyzja użytkownika 12.10.2026, F1 „a”; decyzja
 * przy przełączniku — 13.10.2026).
 *
 * Funkcje czekające na decyzję Marathona są za przełącznikiem: na stagingu
 * włączone, na produkcji wyłączone, dopóki M. mailem nie napisze „wchodzi”
 * — wtedy administrator włącza przełącznik z powodem „decyzja M. z dd.mm”.
 * Każda zmiana wymaga powodu i trafia do historii poniżej oraz do dziennika
 * działań. Zapis tylko dla administratora instancji (mówi o tym API,
 * `/feature-flags/access`); bez niej ekran jest tylko do odczytu.
 *
 * **Decyzja** („wchodzi” / „do rozbudowy” / „odrzucone” / „do decyzji”
 * z komentarzem) jest osobno od stanu: stan mówi, co jest włączone na tym
 * środowisku, decyzja — co postanowiono. Zapis decyzji przełącznika nie
 * przestawia; ekran tylko podpowiada niespójność. Eksport CSV — do maila
 * albo ClickUpa.
 *
 * **Przewodnik** (21.10.2026, przełącznik `guideOverlay`): przy funkcji
 * z przewodnikiem po zmianach przycisk otwiera panel na pierwszym kroku.
 */

export const READ_ONLY_NOTE =
  'Przełączniki i decyzje zmienia tylko administrator — masz dostęp tylko do odczytu.';

export const SCREEN_NOTE =
  'Za przełącznikami są funkcje czekające na decyzję Marathona. Stan (włączony / wyłączony) to tylko to, co działa na tym środowisku; decyzję — wchodzi, do rozbudowy, odrzucone — zapisuje się osobno, z komentarzem. Po decyzji „wchodzi” administrator na produkcji włącza przełącznik z powodem, który wskazuje decyzję; funkcja odrzucona albo do rozbudowy zostaje tam wyłączona. Poprawki błędów idą bez przełącznika. Zmiana stanu działa najpóźniej po minucie.';

/**
 * Przełącznik bez zmiany stanu na tym środowisku. Do 13.10.2026 „bez decyzji —
 * …”; od wprowadzenia decyzji o funkcji „decyzja” znaczy co innego.
 */
export const NO_STATE_CHANGE = 'bez zmian — stan domyślny środowiska';

/** Profil wydania (25.10.2026, S-25.10-02) — zdanie pod nagłówkiem ekranu. */
export const RELEASE_PROFILE_NOTE =
  'Działa profil wydania: wyboru wersji nie ma, a funkcje spoza wydawanych paczek są wyłączone dla wszystkich.';

/** Dymek przy przycisku funkcji spoza wydawanej paczki. */
export const PROFILE_LOCKED_NOTE =
  'Funkcja spoza wydawanej paczki — na tym środowisku zostaje wyłączona i nie da się jej tu włączyć.';

/** Filtr listy po decyzji — `all` = bez filtra. */
type DecisionFilter = DecisionStatus | 'all';

/** Plik eksportu (react-admin dopisuje `.csv`). */
export const EXPORT_FILENAME = 'przelaczniki-funkcji-decyzje';

// Szerokości kolumn przy `tableLayout: fixed` — tabela mieści się przy
// 1366 px (z menu bocznym) bez przewijania w bok; dłuższe teksty zawijają się
// albo są skrócone z pełną treścią w dymku.
// 29.10.2026 (przegląd wyglądu na różnych rozdzielczościach): ostatnia kolumna
// 18% — przy 13% przycisk „Przewodnik” wystawał z komórki o 39 px przy 1280 px;
// „Stan tutaj” 12% — „włączony (domyślnie)” łamał się w środku słowa.
// 02.11.2026 (decyzja Bartka: neutralne teksty w aplikacji): bez kolumny
// „Pozycja” — numery pozycji prezentacji („S-12.10-23”) to wewnętrzny ślad
// decyzji, zostają w rejestrze API i w eksporcie CSV; jej 11% dostały
// „Funkcja” (+8) i „Komentarz decyzji” (+3).
export const COLUMN_WIDTHS = ['30%', '12%', '10%', '19%', '11%', '18%'] as const;
/**
 * Poniżej tej szerokości tabela przewija się w swoim opakowaniu (okno 1024 px
 * z menu) — zamiast ściskać kolumny tak, że przyciski wystają, a słowa łamią się
 * w środku. Przy 1280 px i szerzej mieści się cała.
 */
export const TABLE_MIN_WIDTH = 1000;

/**
 * Wąskie okno (poniżej 1280 px, 29.10.2026, decyzja Bartka: „1024 px — zrób
 * tak, żeby było dobrze i czytelnie”): pięć kolumn zamiast sześciu — „kto /
 * kiedy” pod decyzją — i tabela
 * mieści się przy 1024 px bez przewijania w bok. Ostatnia kolumna jak
 * szeroka (ok. 190 px), żeby „Przewodnik” się mieścił.
 */
export const COLUMN_WIDTHS_NARROW = ['27%', '14%', '15%', '23%', '21%'] as const;
export const TABLE_MIN_WIDTH_NARROW = 880;

/** Chip, którego treść się zawija zamiast wychodzić poza wąską kolumnę. */
const wrapChipSx = { maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } };

/** Skrócenie do dwóch linii — pełna treść w dymku. */
const clampSx = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  wordBreak: 'break-word',
} as const;

export const FeatureFlagList = () => {
  const notify = useNotify();
  const effective = useFeatureFlags();
  const [flags, setFlags] = useState<FeatureFlag[] | null>(null);
  const narrow = useNarrowLayout();
  const widths: readonly string[] = narrow ? COLUMN_WIDTHS_NARROW : COLUMN_WIDTHS;
  const [history, setHistory] = useState<FlagChange[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<FeatureFlag | null>(null);
  const [deciding, setDeciding] = useState<FeatureFlag | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [filter, setFilter] = useState<DecisionFilter>('all');
  // Po zapisie decyzji rozwinięty wiersz czyta historię od nowa.
  const [revision, setRevision] = useState(0);
  // `null` = jeszcze nie wiemy; do czasu odpowiedzi zmiana zamknięta.
  const [canEdit, setCanEdit] = useState<boolean | null>(null);

  useEffect(() => {
    let active = true;
    fetchFlagAccess()
      .then((a) => {
        if (active) setCanEdit(a.canEdit);
      })
      .catch(() => {
        if (active) setCanEdit(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const [f, h] = await Promise.all([fetchFlags(), fetchFlagHistory(null)]);
      setFlags(f);
      setHistory(h);
      setError(null);
    } catch (e) {
      setError(toErrorMessage(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const labelOf = (key: string) => flags?.find((f) => f.key === key)?.label ?? key;
  const defaultEnabled = flags?.[0]?.defaultEnabled;
  const counts = useMemo(() => decisionCounts(flags ?? []), [flags]);
  const visible = useMemo(
    () => (flags ?? []).filter((f) => filter === 'all' || decisionOf(f).status === filter),
    [flags, filter],
  );

  return (
    <Box sx={{ p: 2 }}>
      <Title title={menuLabel('feature-flags')} />
      <Alert severity="info" sx={{ mb: 2 }}>
        {SCREEN_NOTE}
      </Alert>
      <Typography variant="body2" sx={{ mb: 2 }} data-testid="environment-note">
        Na tym środowisku przełącznik bez decyzji administratora jest{' '}
        <strong>{defaultEnabled === undefined ? '…' : defaultEnabled ? 'włączony' : 'wyłączony'}</strong>.{' '}
        {effective?.releaseProfile
          ? RELEASE_PROFILE_NOTE
          : effective?.versionSwitchAvailable && effective.version
            ? `Wybór wersji jest dostępny w menu użytkownika — pracujesz w wersji: ${VERSION_LABELS[effective.version]}.`
            : 'Wyboru wersji tu nie ma — dla wszystkich obowiązuje stan przełączników.'}
      </Typography>
      {canEdit === false && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="read-only-note">
          {READ_ONLY_NOTE}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {/* Paczki wydania (25.10.2026, S-25.10-02) — włącznik całej paczki. */}
      <FeatureBundlesPanel canEdit={canEdit} onChanged={() => void load()} />
      {flags === null && !error ? (
        <CircularProgress size={24} />
      ) : flags !== null && flags.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Brak przełączników — żadna funkcja nie czeka na decyzję.
        </Typography>
      ) : (
        <>
          <Stack
            direction="row"
            spacing={2}
            useFlexGap
            sx={{ mb: 1, alignItems: 'center', flexWrap: 'wrap' }}
          >
            <Typography variant="body2" data-testid="decision-counts">
              Decyzje: {decisionCountsText(counts)}
            </Typography>
            <TextField
              select
              size="small"
              label="Decyzja"
              value={filter}
              onChange={(e) => setFilter(e.target.value as DecisionFilter)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="all">wszystkie</MenuItem>
              {DECISION_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {DECISION_LABELS[s]}
                </MenuItem>
              ))}
            </TextField>
            <Button size="small" onClick={() => downloadCSV(decisionsCsv(visible), EXPORT_FILENAME)}>
              Eksport CSV
            </Button>
          </Stack>
          <Box sx={{ overflowX: 'auto' }} data-testid="feature-flags-scroll">
            <Table size="small" aria-label={menuLabel('feature-flags')} sx={{ tableLayout: 'fixed', width: '100%', minWidth: narrow ? TABLE_MIN_WIDTH_NARROW : TABLE_MIN_WIDTH }}>
              <colgroup>
                {widths.map((w, i) => (
                  <col key={i} style={{ width: w }} />
                ))}
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell>Funkcja</TableCell>
                  <TableCell>Stan tutaj</TableCell>
                  <TableCell>Decyzja</TableCell>
                  <TableCell>Komentarz decyzji</TableCell>
                  {narrow ? null : <TableCell>Kto / kiedy</TableCell>}
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={widths.length}>
                      <Typography variant="body2" color="text.secondary">
                        Żaden przełącznik nie ma takiej decyzji.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {visible.map((f) => {
                  const decision = decisionOf(f);
                  const hint = decisionHint(decision.status, f.enabled);
                  const isOpen = expanded === f.key;
                  return (
                    <Fragment key={f.key}>
                      <TableRow data-testid={`flag-row-${f.key}`}>
                        <TableCell sx={{ wordBreak: 'break-word' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {f.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="div">
                            {f.key}
                          </Typography>
                          {f.bundle && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label="w paczce wydania"
                              data-testid={`flag-bundle-${f.key}`}
                              sx={{ my: 0.25 }}
                            />
                          )}
                          <Tooltip title={f.description} describeChild>
                            <Typography variant="caption" color="text.secondary" component="div" sx={clampSx}>
                              {f.description}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={f.enabled ? 'success' : 'default'}
                            label={flagStateLabel(f.configured ? f.enabled : null, f.defaultEnabled)}
                            sx={wrapChipSx}
                          />
                          {/* Ostatnia zmiana stanu — kto i kiedy przestawił przełącznik
                              na tym środowisku (powód w dymku i w historii). */}
                          {f.lastChange ? (
                            <Tooltip title={`Powód: ${f.lastChange.reason}`}>
                              <Typography variant="caption" color="text.secondary" component="div">
                                {fmtDate(f.lastChange.changedAt)} · {f.lastChange.changedByName ?? '—'}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.secondary" component="div">
                              {NO_STATE_CHANGE}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={DECISION_COLORS[decision.status]}
                            label={DECISION_LABELS[decision.status]}
                            data-testid={`decision-${f.key}`}
                            sx={wrapChipSx}
                          />
                          {hint && (
                            <Tooltip title={hint}>
                              <Typography
                                variant="caption"
                                color="warning.main"
                                component="div"
                                data-testid={`decision-hint-${f.key}`}
                              >
                                stan inny niż decyzja
                              </Typography>
                            </Tooltip>
                          )}
                          {/* Wąskie okno: kto i kiedy zdecydował — pod decyzją. */}
                          {narrow && decision.decidedAt ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              component="div"
                              data-testid={`decision-by-${f.key}`}
                            >
                              {decision.decidedByName ?? '—'} · {fmtDate(decision.decidedAt)}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          {decision.comment ? (
                            <Tooltip title={decision.comment} describeChild>
                              <Typography variant="body2" sx={clampSx}>
                                {decision.comment}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        {narrow ? null : (
                        <TableCell sx={{ wordBreak: 'break-word' }}>
                          {decision.decidedAt ? (
                            <>
                              <Typography variant="body2">{decision.decidedByName ?? '—'}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {fmtDate(decision.decidedAt)}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        )}
                        <TableCell align="right">
                          <Stack spacing={0.5} sx={{ alignItems: 'flex-end' }}>
                            {f.lockedByProfile ? (
                              // Profil wydania (25.10.2026): funkcja spoza wydawanej
                              // paczki zostaje wyłączona — API i tak odmawia 409.
                              <Tooltip title={PROFILE_LOCKED_NOTE}>
                                <span>
                                  <Button size="small" disabled data-testid={`flag-locked-${f.key}`}>
                                    {f.enabled ? 'Wyłącz' : 'Włącz'}
                                  </Button>
                                </span>
                              </Tooltip>
                            ) : (
                              <Button size="small" disabled={canEdit !== true} onClick={() => setEditing(f)}>
                                {f.enabled ? 'Wyłącz' : 'Włącz'}
                              </Button>
                            )}
                            {/* Decyzję zapisuje tylko administrator — bez
                                uprawnienia przycisku nie ma (API i tak 403). */}
                            {canEdit === true && (
                              <Button size="small" onClick={() => setDeciding(f)}>
                                Decyzja
                              </Button>
                            )}
                            <Button
                              size="small"
                              aria-expanded={isOpen}
                              onClick={() => setExpanded(isOpen ? null : f.key)}
                            >
                              Historia
                            </Button>
                            {/* Przewodnik po funkcji (21.10.2026) — otwiera panel
                                przewodnika na pierwszym kroku; bez przewodnika nic. */}
                            <FeatureGate feature="guideOverlay">
                              <GuideFlagButton flagKey={f.key} />
                            </FeatureGate>
                          </Stack>
                        </TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow>
                          <TableCell colSpan={widths.length} sx={{ bgcolor: 'action.hover' }}>
                            <FlagDetails flag={f} revision={revision} defaultEnabled={f.defaultEnabled} />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </>
      )}

      {/* Próba mechanizmu: przełącznik techniczny widziany z interfejsu
          i z API (trasa za strażnikiem). */}
      <FeatureGate feature="featureFlagTrial">
        <TrialCheck />
      </FeatureGate>

      <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
        Historia zmian stanu
      </Typography>
      {history.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Brak zmian — obowiązuje stan domyślny środowiska.
        </Typography>
      ) : (
        <Table size="small" aria-label="Historia zmian">
          <TableHead>
            <TableRow>
              <TableCell>Kiedy</TableCell>
              <TableCell>Kto</TableCell>
              <TableCell>Funkcja</TableCell>
              <TableCell>Zmiana</TableCell>
              <TableCell>Powód</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.map((c) => (
              <TableRow key={c.id}>
                <TableCell>{fmtDate(c.changedAt)}</TableCell>
                <TableCell>{c.changedByName ?? '—'}</TableCell>
                <TableCell>{labelOf(c.key)}</TableCell>
                <TableCell>
                  {flagStateLabel(c.oldValue, defaultEnabled ?? false)} → {flagStateLabel(c.newValue, false)}
                </TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>{c.reason}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {editing && canEdit === true && (
        <ToggleFlagDialog
          flag={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            notify('Przełącznik zapisany', { type: 'success' });
            setRevision((r) => r + 1);
            void load();
            void reloadEffectiveFlags();
          }}
        />
      )}
      {deciding && canEdit === true && (
        <DecisionDialog
          flag={deciding}
          onClose={() => setDeciding(null)}
          onSaved={() => {
            setDeciding(null);
            notify('Decyzja zapisana', { type: 'success' });
            setRevision((r) => r + 1);
            void load();
          }}
        />
      )}
    </Box>
  );
};

/**
 * Rozwinięcie wiersza: pełny opis, pochodzenie, historia stanu i historia
 * decyzji tego przełącznika — czytane przy rozwinięciu.
 */
function FlagDetails({
  flag,
  revision,
  defaultEnabled,
}: {
  flag: FeatureFlag;
  revision: number;
  defaultEnabled: boolean;
}) {
  const [states, setStates] = useState<FlagChange[] | null>(null);
  const [decisions, setDecisions] = useState<DecisionChange[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    Promise.all([fetchFlagHistory(flag.key), fetchDecisionHistory(flag.key)])
      .then(([s, d]) => {
        if (!active) return;
        setStates(s);
        setDecisions(d);
        setError(null);
      })
      .catch((e) => {
        if (active) setError(toErrorMessage(e));
      });
    return () => {
      active = false;
    };
  }, [flag.key, revision]);

  return (
    <Box data-testid={`flag-details-${flag.key}`}>
      {/* Bez „Od: …” (02.11.2026): pochodzenie przełącznika (data, decyzja) to ślad wewnętrzny — zostaje w rejestrze API. */}
      <Typography variant="body2" sx={{ mb: 1 }}>
        {flag.description}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Box sx={{ flex: '1 1 380px', minWidth: 0 }}>
          <Typography variant="subtitle2">Historia decyzji</Typography>
          {decisions === null ? (
            <CircularProgress size={16} />
          ) : decisions.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              Bez decyzji — do decyzji.
            </Typography>
          ) : (
            <Table size="small" aria-label="Historia decyzji" sx={{ tableLayout: 'fixed' }}>
              <TableBody>
                {decisions.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell sx={{ width: '28%' }}>
                      {fmtDate(c.changedAt)}
                      <Typography variant="caption" color="text.secondary" component="div">
                        {c.changedByName ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: '26%' }}>
                      {decisionLabel(c.oldStatus)} → {decisionLabel(c.newStatus)}
                    </TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>{c.comment ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
        <Box sx={{ flex: '1 1 380px', minWidth: 0 }}>
          <Typography variant="subtitle2">Historia stanu</Typography>
          {states === null ? (
            <CircularProgress size={16} />
          ) : states.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              Bez zmian — stan domyślny środowiska.
            </Typography>
          ) : (
            <Table size="small" aria-label="Historia stanu" sx={{ tableLayout: 'fixed' }}>
              <TableBody>
                {states.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell sx={{ width: '28%' }}>
                      {fmtDate(c.changedAt)}
                      <Typography variant="caption" color="text.secondary" component="div">
                        {c.changedByName ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: '26%' }}>
                      {flagStateLabel(c.oldValue, defaultEnabled)} → {flagStateLabel(c.newValue, false)}
                    </TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>{c.reason}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </Stack>
    </Box>
  );
}

/**
 * Okno decyzji: wybór decyzji i komentarz. Komentarz obowiązkowy przy „do
 * rozbudowy”, „odrzucone” i przy powrocie do „do decyzji” (lustro API).
 * Stanu przełącznika nie zmienia — mówi o tym wprost.
 */
function DecisionDialog({
  flag,
  onClose,
  onSaved,
}: {
  flag: FeatureFlag;
  onClose: () => void;
  onSaved: () => void;
}) {
  const current = decisionOf(flag);
  const [status, setStatus] = useState<DecisionStatus>(current.status);
  const [comment, setComment] = useState(current.comment ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const commentRequired =
    status === 'rework' || status === 'rejected' || (status === 'pending' && current.status !== 'pending');

  const save = async () => {
    const refusal = decisionError(current, status, comment);
    if (refusal) {
      setError(refusal);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await saveDecision(flag.key, status, comment.trim());
      onSaved();
    } catch (e) {
      setError(toErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Decyzja: {flag.label}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Teraz: {DECISION_LABELS[current.status]}. Decyzja nie włącza ani nie wyłącza przełącznika — stan zmienia się
          osobno (na produkcji po wydaniu).
        </Typography>
        <RadioGroup
          aria-label="Decyzja"
          value={status}
          onChange={(e) => setStatus(e.target.value as DecisionStatus)}
        >
          {DECISION_STATUSES.map((s) => (
            <FormControlLabel key={s} value={s} control={<Radio size="small" />} label={DECISION_LABELS[s]} />
          ))}
        </RadioGroup>
        <TextField
          label="Komentarz decyzji"
          placeholder={
            status === 'rework'
              ? 'np. dodać filtr po spółce'
              : status === 'rejected'
                ? 'np. klient nie chce tej zmiany'
                : 'np. decyzja Marathonu przekazana mailem'
          }
          helperText={commentRequired ? 'Komentarz jest wymagany' : 'Komentarz opcjonalny'}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          size="small"
          fullWidth
          multiline
          minRows={3}
          required={commentRequired}
          sx={{ mt: 1 }}
          slotProps={{ htmlInput: { maxLength: MAX_DECISION_COMMENT_LENGTH } }}
        />
        {error && (
          <Typography role="alert" color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Anuluj
        </Button>
        <Button onClick={() => void save()} variant="contained" disabled={busy}>
          Zapisz decyzję
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Potwierdzenie próby przełącznika: interfejs widzi go jako włączony, a API? */
function TrialCheck() {
  const [api, setApi] = useState<boolean | null>(null);
  useEffect(() => {
    let active = true;
    void probeTrialRoute().then((ok) => {
      if (active) setApi(ok);
    });
    return () => {
      active = false;
    };
  }, []);
  return (
    <Alert severity="success" sx={{ mt: 2 }} data-testid="trial-check">
      Próba przełącznika: interfejs widzi go jako włączony dla Twojego konta;{' '}
      {api === null ? 'API — sprawdzam…' : api ? 'API też przepuszcza trasę.' : 'API odpowiada, że funkcja jest wyłączona.'}
    </Alert>
  );
}

function ToggleFlagDialog({
  flag,
  onClose,
  onSaved,
}: {
  flag: FeatureFlag;
  onClose: () => void;
  onSaved: () => void;
}) {
  const target = !flag.enabled;
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (reason.trim() === '') {
      setError('Podaj powód zmiany');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await saveFlag(flag.key, target, reason.trim());
      onSaved();
    } catch (e) {
      setError(toErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {target ? 'Włącz' : 'Wyłącz'}: {flag.label}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {flag.description}
        </Typography>
        <TextField
          label="Powód zmiany"
          placeholder={target ? 'np. decyzja Marathonu przekazana mailem' : 'np. funkcja do rozbudowy'}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          size="small"
          fullWidth
          multiline
          minRows={2}
          required
          slotProps={{ htmlInput: { maxLength: 500 } }}
        />
        {error && (
          <Typography role="alert" color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Anuluj
        </Button>
        <Button onClick={() => void save()} variant="contained" disabled={busy}>
          {target ? 'Włącz' : 'Wyłącz'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
